import React from 'react';
import { Loader2, FileText, Sparkles, TrendingUp, TrendingDown, Users, Activity, Heart, Wallet } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { formatStatsDate } from './statisticsDateUtils';

/* ─── Micro sparkline ─── */
function MiniSparkline({ data, color, width = 64, height = 24 }) {
    if (!data || data.length < 2) return null;
    return (
        <div style={{ width, height }} className="opacity-70">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                    <Line type="monotone" dataKey="v" stroke={color} strokeWidth={2.5} dot={false} strokeLinecap="round" />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

/* ─── Elegant KPI Card ─── */
function KpiCard({ title, icon: Icon, value, unit, delta, deltaUnit, accentColor, sparkData, inverse = false }) {
    const hasDelta = delta !== undefined && delta !== 0;

    // Determine the color setup based on inverse mode
    const bgClass = inverse ? `bg-[${accentColor}] text-white` : 'bg-white text-gray-900';
    const textDescClass = inverse ? 'text-white/80' : 'text-gray-500';
    const iconColorClass = inverse ? 'text-white/60' : 'text-gray-400';
    const unitClass = inverse ? 'text-white/70' : 'text-gray-400';
    const borderClass = inverse ? 'border-transparent' : 'border-gray-100/60';

    return (
        <div
            className={`rounded-[2rem] border ${borderClass} ${bgClass} p-6 shadow-[0_8px_30px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-500 relative overflow-hidden group hover:-translate-y-1`}
            style={inverse ? { backgroundColor: accentColor } : {}}
        >
            {/* Subtle glow effect */}
            {!inverse && (
                <div className="absolute top-0 right-0 -m-8 w-32 h-32 rounded-full opacity-10 transition-transform duration-700 group-hover:scale-150 blur-2xl"
                    style={{ backgroundColor: accentColor }} />
            )}

            <div className="relative z-10 flex flex-col h-full justify-between">
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-2">
                        <div className={`p-2.5 rounded-2xl ${inverse ? 'bg-white/10' : 'bg-gray-50'}`}>
                            <Icon size={18} className={inverse ? 'text-white' : 'text-gray-600'} />
                        </div>
                        <h3 className={`text-sm font-bold tracking-wide ${textDescClass}`}>{title}</h3>
                    </div>
                </div>

                <div className="flex items-end justify-between">
                    <div>
                        <div className="flex items-baseline gap-1.5 leading-none">
                            <span className="text-[40px] font-black tracking-tight">{value}</span>
                            <span className={`text-base font-bold ${unitClass}`}>{unit}</span>
                        </div>
                        {hasDelta && (
                            <div className="mt-3 flex items-center gap-1.5">
                                <div className={`flex items-center justify-center w-5 h-5 rounded-full ${delta > 0 ? (inverse ? 'bg-white/20' : 'bg-emerald-50') : (inverse ? 'bg-white/20 text-white' : 'bg-rose-50 text-rose-500')}`}>
                                    {delta > 0
                                        ? <TrendingUp size={12} className={inverse ? 'text-white' : 'text-emerald-500'} />
                                        : <TrendingDown size={12} className={inverse ? 'text-white' : 'text-rose-500'} />
                                    }
                                </div>
                                <span className={`text-xs font-black tracking-wide ${delta > 0 ? (inverse ? 'text-white' : 'text-emerald-600') : (inverse ? 'text-white' : 'text-rose-500')}`}>
                                    {Math.abs(delta)}{deltaUnit}
                                </span>
                            </div>
                        )}
                    </div>
                    {sparkData && <MiniSparkline data={sparkData} color={inverse ? '#FFFFFF' : accentColor} width={70} height={28} />}
                </div>
            </div>
        </div>
    );
}

export default function StatisticsInfographicHero({
    animatedStudentCount,
    animatedAttendanceRate,
    animatedVisitationRate,
    attendanceSparkline,
    avgAttendance,
    chartIcon,
    financeStats,
    isAiLoading,
    latestDataDate,
    onChangePeriodKey,
    onOpenAiBriefing,
    onOpenReport,
    periodOptions = [],
    selectedDepartment,
    selectedPeriodKey,
    theme,
    visitationStats,
}) {
    const accentColor = theme?.primaryHex || '#354F38';
    const deptLabel = selectedDepartment === 'All' ? '전체 부서' : (selectedDepartment || '부서');

    return (
        <div className="mb-2 space-y-8">

            {/* ── Beautiful Header Section ── */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 pt-2">
                <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-100 shadow-sm mb-4">
                        <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: accentColor }} />
                        <span className="text-[11px] font-bold text-gray-500 tracking-wide">데이터 기준: {formatStatsDate(latestDataDate)}</span>
                    </div>
                    <div className="flex items-center gap-4">
                        {chartIcon && (
                            <div className="hidden sm:flex w-14 h-14 rounded-[1.25rem] bg-white text-3xl items-center justify-center border border-gray-100 shadow-sm transform -rotate-3 hover:rotate-0 transition-transform duration-300">
                                <img src={chartIcon} width={28} alt="" />
                            </div>
                        )}
                        <div>
                            <h2 className="text-4xl sm:text-[42px] font-black tracking-[-0.03em] text-gray-900 leading-none">
                                사역 통계
                            </h2>
                            <p className="text-sm font-bold text-gray-400 mt-2 tracking-wide">
                                {deptLabel}의 핵심 성장 지표를 확인하세요
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3 w-full md:w-auto overflow-x-auto pb-2 md:pb-0 hide-scrollbar">
                    {/* Glass Period Tabs */}
                    <div className="flex items-center bg-white rounded-full p-1 border border-gray-100 shadow-sm shrink-0">
                        {periodOptions.map((opt) => {
                            const active = opt.key === selectedPeriodKey;
                            return (
                                <button
                                    key={opt.key}
                                    onClick={() => onChangePeriodKey(opt.key)}
                                    className={`h-10 px-5 rounded-full text-[13px] font-bold transition-all duration-300 ${active ? 'bg-gray-900 text-white shadow-md' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                                        }`}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>

                    <button
                        onClick={onOpenReport}
                        className="shrink-0 h-10 w-10 sm:w-auto sm:px-5 rounded-full bg-white text-gray-600 border border-gray-100 shadow-sm hover:border-gray-200 hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                    >
                        <FileText size={16} />
                        <span className="hidden sm:inline text-[13px] font-bold">상세 보고서</span>
                    </button>

                    <button
                        onClick={onOpenAiBriefing}
                        disabled={isAiLoading}
                        className={`shrink-0 h-10 w-10 sm:w-auto sm:px-5 rounded-full text-[13px] font-bold transition-all shadow-md flex items-center justify-center gap-2 ${isAiLoading ? 'bg-gray-100 text-gray-400' : 'text-white hover:shadow-lg hover:-translate-y-0.5'
                            }`}
                        style={!isAiLoading ? { backgroundColor: accentColor } : undefined}
                    >
                        {isAiLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                        <span className="hidden sm:inline">AI 브리핑</span>
                    </button>
                </div>
            </div>

            {/* ── Massive Bold KPIs (4 Cards) ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-4">
                <KpiCard
                    title="등록 인원"
                    icon={Users}
                    value={animatedStudentCount}
                    unit="명"
                    accentColor={accentColor}
                />

                {/* Inverse styling for the most important metric */}
                <KpiCard
                    title="평균 출석률"
                    icon={Activity}
                    value={animatedAttendanceRate}
                    unit="%"
                    delta={avgAttendance.delta}
                    deltaUnit="%"
                    accentColor={accentColor}
                    sparkData={attendanceSparkline}
                    inverse={true}
                />

                <KpiCard
                    title="심방 커버리지"
                    icon={Heart}
                    value={animatedVisitationRate}
                    unit="%"
                    delta={visitationStats.delta}
                    deltaUnit="명"
                    accentColor={accentColor}
                />

                <KpiCard
                    title="재정 잔고"
                    icon={Wallet}
                    value={Math.abs(financeStats.balance) >= 10000
                        ? `${Math.round(financeStats.balance / 10000).toLocaleString()}만`
                        : financeStats.balance.toLocaleString()
                    }
                    unit="원"
                    delta={financeStats.delta !== 0 ? (financeStats.delta > 0 ? 1 : -1) : 0}
                    deltaUnit=""
                    accentColor={financeStats.balance < 0 ? '#EF4444' : accentColor}
                />
            </div>

        </div>
    );
}
